import React, { useLayoutEffect } from 'react';

import { useFiltersContext } from 'components/Filters/FiltersContext';
import { RangeFilterConfig, RangeState } from 'components/Filters/types';
import {
  NumberInputField,
  NumberInputFieldProps,
} from '@lotlinx-aws/sparky-component-library/Sparky/NumberInputField';
import Text from '@lotlinx-aws/sparky-component-library/Sparky/Text';

import styles from './RangeOptions.module.scss';

interface RangeOptionsProps {
  config: RangeFilterConfig<unknown>;
}

/**
 * Range filter inputs
 */
export default function RangeOptions({ config }: RangeOptionsProps) {
  const { getFilterState, setFilterState, clearFilter, isLoading } = useFiltersContext();
  const state = getFilterState(config.id) as RangeState | undefined;
  const [min, setMin] = React.useState<number | undefined>(
    typeof state?.min === 'number' ? state?.min : undefined,
  );
  const [max, setMax] = React.useState<number | undefined>(
    typeof state?.min === 'number' ? state?.min : undefined,
  );

  const updateFilterValue = () => {
    if (min === undefined && max === undefined) {
      clearFilter(config.id);
    } else if (min === undefined || max === undefined || min <= max) {
      setFilterState(config.id, { min: min, max: max });
    } else {
      // If min is larger than max, switch places
      setFilterState(config.id, { min: max, max: min });
    }
  };

  useLayoutEffect(() => {
    setMin(typeof state?.min === 'number' ? state?.min : undefined);
    setMax(typeof state?.max === 'number' ? state?.max : undefined);
  }, [state]);

  const commonProps: Omit<NumberInputFieldProps, 'id' | 'label'> = {
    disabled: isLoading,
    size: 'sm' as any,
    onBlur: () => updateFilterValue(),
    onKeyDown: (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        updateFilterValue();
      }
    },
  };

  const toInteger = (value: number | undefined) =>
    value !== undefined ? Math.trunc(value) : undefined;

  return (
    <div className={styles.rangeOptions}>
      <NumberInputField
        id={`${config.id}-min`}
        label='Min.'
        placeholder='Min'
        name='min'
        showNumberCaret={true}
        thousandSeparator={true}
        value={min}
        onValueChange={(value) =>
          setMin(config.integerOnly ? toInteger(value.floatValue) : value.floatValue)
        }
        prefix={config.rangePrefix}
        suffix={config.rangeSuffix}
        className={styles.minInput}
        decimalScale={config.integerOnly ? 0 : undefined}
        {...commonProps}
      />
      <Text color='tertiary' className={styles.connector}>
        -
      </Text>
      <NumberInputField
        id={`${config.id}-max`}
        label='Max.'
        placeholder='Max'
        name='max'
        showNumberCaret={true}
        thousandSeparator={true}
        value={max}
        onValueChange={(value) =>
          setMax(config.integerOnly ? toInteger(value.floatValue) : value.floatValue)
        }
        prefix={config.rangePrefix}
        suffix={config.rangeSuffix}
        className={styles.maxInput}
        decimalScale={config.integerOnly ? 0 : undefined}
        {...commonProps}
      />
    </div>
  );
}
